import {
  DeleteConfig,
  HiddenGeneralField,
  QuizOptionsConfig,
} from '@/components/ManageDynamicPage/DynamicPageSettingsModal';
import { SnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { UserContext } from '@/contexts/UserContext.tsx';
import {
  updateDynamicPage,
  updateDynamicPageMetadata,
} from '@/services/DynamicPagesService.ts';
import * as Sentry from '@sentry/react';
import { DynamicPage } from '@superfunnel/types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface DynamicPageSettingsContextProps {
  dynamicPageId: string | undefined;
  dynamicPage: DynamicPage | undefined;
  updatePage: (data: any, mergeFields?: string[]) => Promise<void>;
  updateMetadata: (metadata: {
    path?: string;
    alternatePathes?: string[];
  }) => Promise<void>;
  /** Fields to hide in the General tab */
  hideGeneralFields?: HiddenGeneralField[];
  /** Custom delete configuration */
  deleteConfig?: DeleteConfig;
  /** Quiz options configuration */
  quizOptions?: QuizOptionsConfig;
}

const DynamicPageSettingsContext =
  createContext<DynamicPageSettingsContextProps | null>(null);

export function useDynamicPageSettingsContext() {
  const context = useContext(DynamicPageSettingsContext);
  if (context === null) {
    throw new Error(
      'useDynamicPageSettingsContext must be used within a DynamicPageSettingsProvider',
    );
  }
  return context;
}

interface DynamicPageSettingsProviderProps {
  dynamicPageId: string | undefined;
  dynamicPage: DynamicPage | undefined;
  children: ReactNode;
  /** Optional custom update function. If not provided, uses default updateDynamicPage service */
  onUpdatePage?: (data: any, mergeFields?: string[]) => Promise<void>;
  /** Optional custom metadata update function. If not provided, uses default updateDynamicPageMetadata service */
  onUpdateMetadata?: (metadata: {
    path?: string;
    alternatePathes?: string[];
  }) => Promise<void>;
  /** Fields to hide in the General tab */
  hideGeneralFields?: HiddenGeneralField[];
  /** Custom delete configuration */
  deleteConfig?: DeleteConfig;
  /** Quiz options configuration */
  quizOptions?: QuizOptionsConfig;
}

export function DynamicPageSettingsProvider({
  dynamicPageId,
  dynamicPage: initialDynamicPage,
  children,
  onUpdatePage,
  onUpdateMetadata,
  hideGeneralFields,
  deleteConfig,
  quizOptions,
}: DynamicPageSettingsProviderProps) {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);

  // Local state to track dynamic page data for immediate UI updates
  const [dynamicPage, setDynamicPage] = useState<DynamicPage | undefined>(
    initialDynamicPage,
  );

  // Sync with prop changes
  useEffect(() => {
    setDynamicPage(initialDynamicPage);
  }, [initialDynamicPage]);

  const updatePage = useCallback(
    async (data: any, mergeFields?: string[]) => {
      // Use custom handler if provided
      if (onUpdatePage) {
        await onUpdatePage(data, mergeFields);
        // Update local state
        setDynamicPage((prev) => (prev ? { ...prev, ...data } : prev));
        return;
      }
      // Default behavior
      if (!selectedAccount || !dynamicPageId) return;
      try {
        await updateDynamicPage(
          selectedAccount.id,
          dynamicPageId,
          data,
          mergeFields,
        );
        // Update local state
        setDynamicPage((prev) => (prev ? { ...prev, ...data } : prev));
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        show('Failed to update');
        throw e;
      }
    },
    [selectedAccount, dynamicPageId, show, onUpdatePage],
  );

  const updateMetadata = useCallback(
    async (metadata: { path?: string; alternatePathes?: string[] }) => {
      // Use custom handler if provided
      if (onUpdateMetadata) {
        await onUpdateMetadata(metadata);
        // Update local state
        setDynamicPage((prev) => (prev ? { ...prev, ...metadata } : prev));
        return;
      }
      // Default behavior
      if (!selectedAccount || !dynamicPageId) return;
      try {
        await updateDynamicPageMetadata(
          selectedAccount.id,
          dynamicPageId,
          metadata,
        );
        // Update local state
        setDynamicPage((prev) => (prev ? { ...prev, ...metadata } : prev));
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        show((e as any).message || 'Failed to update');
        throw e;
      }
    },
    [selectedAccount, dynamicPageId, show, onUpdateMetadata],
  );

  const value = useMemo(
    () => ({
      dynamicPageId,
      dynamicPage,
      updatePage,
      updateMetadata,
      hideGeneralFields,
      deleteConfig,
      quizOptions,
    }),
    [
      dynamicPageId,
      dynamicPage,
      updatePage,
      updateMetadata,
      hideGeneralFields,
      deleteConfig,
      quizOptions,
    ],
  );

  return (
    <DynamicPageSettingsContext.Provider value={value}>
      {children}
    </DynamicPageSettingsContext.Provider>
  );
}
